// React-router setup for the marketing site

import React from "react";
import { IndexRoute, Route } from "react-router";
import * as Redux from "redux";
import { withProps } from "../shared/helpers/reactHelpers";
import { addErrorBoundaries, requireAuth } from "../shared/routes/routeHelpers";

// VIEWS
import { ItinerarySummaryGalleryDialog, itinerarySummaryGalleryDialogOnEnter }
  from "../shared/itinerarySummaryGalleryDialog";
import { RoutesInvoker } from "./routesInvoker";
import { AboutIndex, AboutMeetTheTeam, AboutOverview } from './views/about';
import { meetTheTeamOnEnter } from "./views/about/meetTheTeam/meetTheTeam";
import { Login, LoginOnEnter } from "./views/account/login";
import { default as Logout } from "./views/account/logout";
import RequestPasswordReset from "./views/account/requestPasswordReset";
import ResetPassword from "./views/account/resetPassword";
import { CustomerEnquiries, customerEnquiriesOnEnter } from "./views/customer/customerEnquiries";
import * as CustomerEnquiry from "./views/customer/customerEnquiry";
import { CustomerIndex } from "./views/customer/customerIndex";
import { Destinations, destinationsOnEnter } from "./views/destinations";
import { Bulma } from "./views/dev/bulma";
import { DevDirectory } from "./views/dev/devDirectory";
import { EnquireLegacyRedirect } from "./views/enquire";
import { default as Enquire2, enquirePageOnEnter as enquire2OnEnter } from "./views/enquire2";
import { Home, homeOnEnter } from "./views/home";
import { Index, indexOnEnter } from "./views/index";
import { InspirationIndex, inspirationIndexOnEnter } from "./views/inspiration/index";
import { ReviewDetail, reviewDetailOnEnter } from "./views/inspiration/reviewDetail";
import { inspirationSingleOnEnter, SingleItemLanding } from "./views/inspiration/singleItemLanding";
import * as Country from "./views/locations/country";
import { ItineraryIndex, itineraryIndexOnEnter } from "./views/locations/itinerary/itineraryIndex";
import { ItinerarySummary } from "./views/locations/itinerary/itinerarySummary";
import { RegionGallery } from "./views/locations/region/regionGallery";
import { RegionGalleryDialog, regionGalleryDialogOnEnter } from "./views/locations/region/regionGalleryDialog";
import { RegionIndex, regionIndexOnEnter } from "./views/locations/region/regionIndex";
import { RegionOverview } from "./views/locations/region/regionOverview";
import {
  LtoDetails, ltoDetailsOnEnter, LtoIndex, ltoIndexOnEnter, LtoPtr, LtoTerms, ltoTermsOnEnter,
} from "./views/lto";
import { IShellComponentProps, Shell } from "./views/shell";
import { Accessibility } from "./views/static/accessibility";
import { BecomeAPartner } from "./views/static/becomeAPartner";
import { Contact } from "./views/static/contact";
import { Privacy } from "./views/static/privacy";
import { StaticIndex } from "./views/static/staticIndex";
import { TermsAndConditions } from "./views/static/termsAndConditions";
import { TermsOfUse } from "./views/static/termsOfUse";
import { NotFound } from "./views/system/notFound";
import { Version } from "./views/system/versionPage";

export function routeFactory(store: Redux.Store<IWebStoreState>, serverRender: boolean) {
  const newShell = (props?: IShellComponentProps) => withProps(Shell, props);
  const fixedShell = newShell();
  const enquire2Shell = newShell({ className: "minimal-shell" }); // TODO RR4 will make this composition better

  const routes = (
    <> {/* JSX expressions must have one parent element (so this groups the two top-level Routes) */}
      <Route path="/version" component={Version} />
      <Route path="/" component={Index} onEnter={indexOnEnter(store, serverRender)}>
        <Route component={enquire2Shell}>
          <Route path="enquire" component={EnquireLegacyRedirect} />
          <Route path="plan-your-trip" component={Enquire2} onEnter={enquire2OnEnter(store, serverRender)} />
          <Route
            path="plan-your-trip/:locationSlug"
            component={Enquire2}
            onEnter={enquire2OnEnter(store, serverRender)}
          />
        </Route>

        <Route component={fixedShell}>
          <IndexRoute component={Home} onEnter={homeOnEnter(store, serverRender)} />
          <Route path="destinations" component={Destinations} onEnter={destinationsOnEnter(store, serverRender)} />

          <Route
            path="destinations/:regionSlug"
            component={RegionIndex}
            onEnter={regionIndexOnEnter(store, serverRender)}
          >
            <IndexRoute component={RegionOverview} />
            <Route path="gallery" component={RegionGallery}>
              <Route
                path=":fileName"
                component={RegionGalleryDialog}
                onEnter={regionGalleryDialogOnEnter(store, serverRender)}
              />
            </Route>
          </Route>

          <Route
            path="destinations/:regionSlug/:countrySlug"
            component={Country.Index}
            onEnter={Country.indexOnEnter(store, serverRender)}
          >
            <IndexRoute component={Country.Overview} />
            <Route
              path="our-experts"
              component={Country.OurExperts}
            />
            <Route
              path="reviews"
              component={Country.ReviewArchive}
              onEnter={Country.reviewArchiveOnEnter(store, serverRender)}
            />
            <Route
              path="trip-ideas"
              component={Country.TripIdeas}
              onEnter={Country.tripIdeasOnEnter(store, serverRender)}
            />
            <Route path="gallery" component={Country.Gallery}>
              <Route
                path=":fileName"
                component={Country.GalleryDialog}
                onEnter={Country.galleryDialogOnEnter(store, serverRender)}
              />
            </Route>
          </Route>


          <Route
            path="itinerary/:itineraryId"
            component={ItineraryIndex}
            onEnter={itineraryIndexOnEnter(store, serverRender)}
          >
            <IndexRoute component={ItinerarySummary} />
            <Route
              path="gallery/:fileName"
              component={ItinerarySummaryGalleryDialog}
              onEnter={itinerarySummaryGalleryDialogOnEnter(store, serverRender)}
            />
          </Route>

          <Route path="local-experts/:ltoSlug" component={LtoIndex} onEnter={ltoIndexOnEnter(store, serverRender)} >
            <IndexRoute component={LtoDetails} onEnter={ltoDetailsOnEnter(store, serverRender)} />
            <Route path="ptr" component={LtoPtr} />
            <Route path="terms" component={LtoTerms} onEnter={ltoTermsOnEnter(store, serverRender)} />
          </Route>

          <Route path="inspiration">
            <IndexRoute
              component={InspirationIndex}
              onEnter={inspirationIndexOnEnter(store, serverRender)}
            />
            <Route
              path=":type/:id"
              component={SingleItemLanding}
              onEnter={inspirationSingleOnEnter(store, serverRender)}
            />
            <Route
              path="review/:id/:slug"
              component={ReviewDetail}
              onEnter={reviewDetailOnEnter(store, serverRender)}
            />
            <Route
              path=":type/:id/:slug"
              component={SingleItemLanding}
              onEnter={inspirationSingleOnEnter(store, serverRender)}
            />
          </Route>

          <Route path="about" component={StaticIndex}>
            <Route path="contact" component={Contact} />
            <Route path="privacy" component={Privacy} />
            <Route path="terms" component={TermsOfUse} />
            <Route path="terms-and-conditions" component={TermsAndConditions} />
            <Route path="accessibility" component={Accessibility} />
            <Route path="become-a-partner" component={BecomeAPartner} />
          </Route>

          <Route path="about-us" component={AboutIndex}>
            <IndexRoute component={AboutOverview} />
            <Route
              path="meet-the-team"
              component={AboutMeetTheTeam}
              onEnter={meetTheTeamOnEnter(store, serverRender)}
            />
          </Route>

          <Route
            path="customer"
            component={CustomerIndex}
            onEnter={requireAuth(UserType.Customer, store, RoutesInvoker.login())}
          >
            <IndexRoute component={CustomerEnquiries} onEnter={customerEnquiriesOnEnter(store, serverRender)} />
            <Route
              path="enquiries/:enquiryId"
              component={CustomerEnquiry.CustomerEnquiryIndex}
              onEnter={CustomerEnquiry.customerEnquiryIndexOnEnter(store, serverRender)}
            >
              <IndexRoute
                component={CustomerEnquiry.Home}
                onEnter={CustomerEnquiry.homeOnEnter(store, serverRender)}
              />
              <Route
                path="messages"
                component={CustomerEnquiry.Messages}
                onEnter={CustomerEnquiry.MessagesOnEnter(store, serverRender)}
              />
              <Route
                path="itinerary"
                component={CustomerEnquiry.ItineraryRedirect}
              />
              <Route
                path="itinerary/:itineraryQuoteId"
                component={CustomerEnquiry.Itinerary}
                onEnter={CustomerEnquiry.itineraryOnEnter(store, serverRender)}
              >
                <Route
                  path="gallery/:fileName"
                  component={ItinerarySummaryGalleryDialog}
                  onEnter={itinerarySummaryGalleryDialogOnEnter(store, serverRender)}
                />
              </Route>
              <Route
                path="payment"
                component={CustomerEnquiry.Payment}
                onEnter={CustomerEnquiry.paymentOnEnter(store, serverRender)}
              />
              <Route
                path="make-payment"
                component={CustomerEnquiry.MakePayment}
                onEnter={CustomerEnquiry.paymentOnEnter(store, serverRender)}
              />
              <Route
                path="traveller-details"
                component={CustomerEnquiry.TravellerDetails}
                onEnter={CustomerEnquiry.travellerDetailsOnEnter(store, serverRender)}
              />
              <Route
                path="review"
                component={CustomerEnquiry.Review}
              />
              <Route
                path="booking-terms"
                component={CustomerEnquiry.BookingTerms}
                onEnter={CustomerEnquiry.bookingTermsOnEnter(store, serverRender)}
              />
            </Route>
          </Route>

          <Route path="dev">
            <IndexRoute component={DevDirectory} />
            <Route path="bulma" component={Bulma} />
          </Route>
        </Route>

        <Route path="account">
          <Route
            path="login"
            component={Login}
            onEnter={LoginOnEnter(store, serverRender)}
          />
          <Route path="logout" component={Logout} />
          <Route path="request-forgotten-password" component={RequestPasswordReset} />
          <Route path="forgotten-password" component={ResetPassword} />
        </Route>

        <Route path="*" component={NotFound} />
      </Route>
    </>
  );

  return addErrorBoundaries(routes);
}
